import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import {
  AlertComponent,
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormLabelDirective,
  RowComponent,
  SpinnerComponent
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  HealthToolGroup,
  SupportArticle,
  SupportAttachment,
  SupportAttachmentUpload,
  SupportTicket,
  SupportTicketService
} from '../../../services/support-ticket.service';
import { SupportUnreadService } from '../../../services/support-unread.service';

type ViewMode = 'list' | 'detail' | 'new';

/**
 * One page for both Health Tools entries (Health Concierge, Ask Dr. Kristy).
 * Which Zammad group it talks to comes from the route data, so the two sidebar
 * links share this component instead of duplicating it.
 */
@Component({
  selector: 'app-health-tools-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardBodyComponent,
    ButtonDirective,
    BadgeComponent,
    AlertComponent,
    SpinnerComponent,
    FormControlDirective,
    FormLabelDirective
  ],
  templateUrl: './health-tools-page.component.html',
  styleUrls: ['./health-tools-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HealthToolsPageComponent {
  private logger = new Logger('HealthToolsPageComponent');
  private route = inject(ActivatedRoute);
  private support = inject(SupportTicketService);
  private unread = inject(SupportUnreadService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** Mirrors Api::SupportController — keep both limits in step. */
  static readonly MAX_ATTACHMENTS = 5;
  static readonly MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

  readonly mode = signal<ViewMode>('list');
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly notice = signal<string | null>(null);

  readonly tickets = signal<SupportTicket[]>([]);
  readonly ticket = signal<SupportTicket | null>(null);
  readonly articles = signal<SupportArticle[]>([]);

  /** i18n key prefix of the active tool, e.g. "health_tools.concierge". */
  readonly toolKey = signal<string>('health_tools.concierge');
  readonly group = signal<HealthToolGroup>('health_concierge');
  /**
   * Extra groups whose tickets are listed alongside `group` — the support hub's
   * "My requests" shows the Health Concierge and general support in ONE list
   * (customer answer R2-9). Creating stays on `group`; when `listOnly` is set the
   * page offers no "new request" at all (the hub's ways do that).
   */
  readonly extraGroups = signal<HealthToolGroup[]>([]);
  readonly listOnly = signal(false);

  // Composer state (shared by "new request" and "reply")
  readonly subject = signal('');
  readonly message = signal('');
  readonly attachments = signal<SupportAttachmentUpload[]>([]);

  constructor() {
    this.route.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.group.set(data['group'] as HealthToolGroup);
        this.toolKey.set(data['toolKey'] as string);
        this.extraGroups.set((data['extraGroups'] as HealthToolGroup[] | undefined) ?? []);
        this.listOnly.set(data['listOnly'] === true);
        this.resetToList();
        // `?new=1` (from the support hub or the dashboard hero) opens the composer
        // straight away; `?q=` carries the question typed into the hero.
        const query = this.route.snapshot.queryParamMap;
        if (query.get('new') === '1' && !this.listOnly()) {
          this.startNewRequest();
          const q = (query.get('q') ?? '').trim();
          if (q) this.message.set(q);
        }
      });
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  resetToList(): void {
    this.mode.set('list');
    this.ticket.set(null);
    this.articles.set([]);
    this.clearComposer();
    this.loadTickets();
  }

  startNewRequest(): void {
    this.clearComposer();
    this.clearMessages();
    this.mode.set('new');
  }

  openTicket(ticket: SupportTicket): void {
    this.clearComposer();
    this.clearMessages();
    this.mode.set('detail');
    this.loading.set(true);

    this.support.get(ticket.id).subscribe({
      next: response => {
        this.ticket.set(response.ticket);
        this.articles.set(response.articles);
        this.loading.set(false);
        // Clears the sidebar badge for this ticket.
        this.unread.markSeen(response.ticket);
      },
      error: err => {
        this.logger.error('Failed to load ticket', err);
        this.error.set(this.translate.translate('health_tools.error_generic'));
        this.loading.set(false);
        this.mode.set('list');
      }
    });
  }

  // ── Data ─────────────────────────────────────────────────────────────────

  private loadTickets(): void {
    this.loading.set(true);
    const groups = [this.group(), ...this.extraGroups()];
    forkJoin(groups.map(g => this.support.list(g))).subscribe({
      next: responses => {
        const all = responses.flatMap(r => r.tickets ?? []);
        // Newest activity first across groups; a single group keeps the server's order.
        if (groups.length > 1) all.sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
        this.tickets.set(all);
        this.loading.set(false);
      },
      error: err => {
        this.logger.error('Failed to load tickets', err);
        this.error.set(this.translate.translate('health_tools.error_generic'));
        this.loading.set(false);
      }
    });
  }

  submitNewRequest(): void {
    const subject = this.subject().trim();
    const message = this.message().trim();

    if (!subject || !message) {
      this.error.set(this.translate.translate('health_tools.error_required'));
      return;
    }

    this.busy.set(true);
    this.clearMessages();

    this.support.create(this.group(), subject, message, this.attachments()).subscribe({
      next: () => {
        this.busy.set(false);
        this.notice.set(this.translate.translate('health_tools.created'));
        this.resetToList();
      },
      error: err => {
        this.logger.error('Failed to create ticket', err);
        this.busy.set(false);
        this.error.set(this.apiError(err));
      }
    });
  }

  submitReply(): void {
    const current = this.ticket();
    const message = this.message().trim();

    if (!current || !message) {
      this.error.set(this.translate.translate('health_tools.error_required'));
      return;
    }

    this.busy.set(true);
    this.clearMessages();

    this.support.reply(current.id, message, this.attachments()).subscribe({
      next: response => {
        this.articles.update(articles => [...articles, response.article]);
        this.clearComposer();
        this.busy.set(false);
      },
      error: err => {
        this.logger.error('Failed to send reply', err);
        this.busy.set(false);
        this.error.set(this.apiError(err));
      }
    });
  }

  toggleState(): void {
    const current = this.ticket();
    if (!current) {
      return;
    }

    const next = this.isClosed(current) ? 'open' : 'closed';
    this.busy.set(true);
    this.clearMessages();

    this.support.setState(current.id, next).subscribe({
      next: response => {
        this.ticket.set(response.ticket);
        this.busy.set(false);
      },
      error: err => {
        this.logger.error('Failed to change ticket state', err);
        this.busy.set(false);
        this.error.set(this.apiError(err));
      }
    });
  }

  downloadAttachment(article: SupportArticle, attachment: SupportAttachment): void {
    const current = this.ticket();
    if (!current) {
      return;
    }

    this.support.downloadAttachment(current.id, article.id, attachment.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        this.logger.error('Failed to download attachment', err);
        this.error.set(this.translate.translate('health_tools.error_generic'));
      }
    });
  }

  // ── Attachments ──────────────────────────────────────────────────────────

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = ''; // allow picking the same file again after removing it

    if (this.attachments().length + files.length > HealthToolsPageComponent.MAX_ATTACHMENTS) {
      this.error.set(this.translate.translate('health_tools.error_attachment_count'));
      return;
    }

    for (const file of files) {
      if (file.size > HealthToolsPageComponent.MAX_ATTACHMENT_BYTES) {
        this.error.set(this.translate.translate('health_tools.error_attachment_size'));
        continue;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.attachments.update(current => [...current, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          data: String(reader.result ?? '')
        }]);
      };
      reader.readAsDataURL(file);
    }
  }

  removeAttachment(index: number): void {
    this.attachments.update(current => current.filter((_, i) => i !== index));
  }

  // ── Helpers used by the template ─────────────────────────────────────────

  isClosed(ticket: SupportTicket): boolean {
    return ['closed', 'merged', 'removed'].includes(ticket.state?.toLowerCase());
  }

  stateColor(ticket: SupportTicket): string {
    return this.isClosed(ticket) ? 'secondary' : 'success';
  }

  /** True when support replied after the member last opened the ticket. */
  hasNewReply(ticket: SupportTicket): boolean {
    if (!ticket.last_agent_contact_at) {
      return false;
    }
    if (!ticket.last_customer_contact_at) {
      return true;
    }
    return new Date(ticket.last_agent_contact_at) > new Date(ticket.last_customer_contact_at);
  }

  isHtml(article: SupportArticle): boolean {
    return (article.content_type ?? '').includes('html');
  }

  formatSize(size?: number | string): string {
    const bytes = Number(size ?? 0);
    if (!bytes) {
      return '';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  clearMessages(): void {
    this.error.set(null);
    this.notice.set(null);
  }

  private clearComposer(): void {
    this.subject.set('');
    this.message.set('');
    this.attachments.set([]);
  }

  private apiError(err: unknown): string {
    const message = (err as { error?: { error?: string } })?.error?.error;
    return message || this.translate.translate('health_tools.error_generic');
  }
}
